import React from 'react';
import { PageLayout } from '../../components/layout/PageLayout';

export default function Security() {
  return (
    <PageLayout
      title="Security"
      subtitle="How Paygent protects your business and your customers' data."
      badge="TRUST & COMPLIANCE"
    >
      <div className="max-w-5xl mx-auto">
        {/* Compliance Badges */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-20">
          {[
            { title: "PCI DSS Level 1", desc: "Highest certification in the payments industry", icon: "ri-shield-check-line", color: "#2563EB" },
            { title: "SOC 2 Type II", desc: "Audited security controls and processes", icon: "ri-lock-line", color: "#7C3AED" },
            { title: "GDPR Compliant", desc: "Full European data protection adherence", icon: "ri-eu-line", color: "#0D9488" },
            { title: "99.99% Uptime SLA", desc: "Enterprise-grade infrastructure reliability", icon: "ri-server-line", color: "#F59E0B" },
          ].map(b => (
            <div key={b.title} className="bg-white p-6 rounded-2xl border border-gray-100 text-center shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4" style={{ background: `${b.color}15` }}>
                <i className={`${b.icon} text-xl w-6 h-6 flex items-center justify-center`} style={{ color: b.color }} />
              </div>
              <h3 className="font-bold text-gray-900 mb-1">{b.title}</h3>
              <p className="text-sm text-gray-500">{b.desc}</p>
            </div>
          ))}
        </div>

        {/* Security Practices */}
        <div className="space-y-8">
          {[
            { title: "Encryption", desc: "All data is encrypted with AES-256 at rest and TLS 1.3 in transit. We enforce HSTS and certificate pinning across all API endpoints.", icon: "ri-lock-password-line" },
            { title: "Tokenization", desc: "Sensitive cardholder data is replaced with non-reversible tokens immediately upon ingestion. Raw PANs never touch your servers or ours beyond the initial encryption boundary.", icon: "ri-key-2-line" },
            { title: "Network Security", desc: "Our infrastructure runs in isolated VPCs with strict security groups, WAF protection, DDoS mitigation, and 24/7 intrusion detection systems.", icon: "ri-radar-line" },
            { title: "Access Controls", desc: "All internal access requires multi-factor authentication, role-based permissions, and is logged in immutable audit trails. We follow the principle of least privilege.", icon: "ri-user-settings-line" },
            { title: "Vulnerability Management", desc: "We conduct regular penetration tests, automated vulnerability scans, and participate in a responsible disclosure program. Critical patches are deployed within 4 hours.", icon: "ri-bug-line" },
            { title: "Incident Response", desc: "Our security team maintains a documented incident response plan with defined severity levels, escalation paths, and communication procedures. Affected customers are notified within 72 hours of a confirmed breach.", icon: "ri-alarm-warning-line" },
          ].map(s => (
            <div key={s.title} className="flex gap-6 p-6 bg-white rounded-2xl border border-gray-100 shadow-sm">
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0 mt-1">
                <i className={`${s.icon} text-blue-600 w-5 h-5 flex items-center justify-center`} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{s.title}</h3>
                <p className="text-gray-600 leading-relaxed">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Reporting */}
        <div className="mt-16 bg-gradient-to-r from-blue-600 to-indigo-700 rounded-[2rem] p-10 md:p-14 text-white text-center">
          <h2 className="text-2xl font-bold mb-4">Report a Vulnerability</h2>
          <p className="text-blue-100 mb-6 max-w-xl mx-auto">If you've discovered a security issue, please disclose it responsibly. We take all reports seriously and will respond within 24 hours.</p>
          <a href="mailto:security@gopaygent.com" className="inline-block px-8 py-3 bg-white text-blue-700 font-bold rounded-xl hover:bg-blue-50 transition-colors">security@gopaygent.com</a>
        </div>
      </div>
    </PageLayout>
  );
}
